import gsap from "gsap"
import type { AnimationI, TickerI } from "tweenly"
import { applyJoinedScrollTexts } from "tweenly"
import { initSubTimelineTween } from "../tween.helpers"
import { BaseTicker } from "./BaseTicker"
import { ScrollCtx } from "./types"

export class ScrollDownTicker extends BaseTicker {
    private readonly maskObject: TickerI
    private readonly animationGroup: AnimationI[]
    private readonly textListNode: HTMLElement | null
    private readonly scrollTime: number
    private readonly scrollPause: number

    constructor(args: ScrollCtx) {
        super(args.timeline, args.objectId, args.joinedTexts, args.childTexts, args.assets)
        this.maskObject = args.maskObject
        this.animationGroup = args.animationGroup
        this.textListNode = args.textListNode
        this.scrollTime = args.scrollTime
        this.scrollPause = args.scrollPause
    }
    //needed for prevent glitching
    private killTweens() {
        gsap.killTweensOf(`#${this.objectId}`)
        for (const childId of this.maskObject.childIds) {
            gsap.killTweensOf(`#${childId}`)
        }
    }

    protected intro() {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode } = this

        timeline.set(`#${objectId}`, { transform: "translateY(-100%)" })

        if (textListNode) {
            timeline.set(`#${textListNode.id}`, {
                transform: "translateY(-100%)",
            })
            for (const animation of this.animationGroup) {
                if (animation.subTimelineType?.startsWith("scrollDown")) {
                    initSubTimelineTween(animation, objectId, timeline)
                }
            }
        }

        const oldCopy = document.getElementById(objectId + "-copy")
        if (oldCopy) oldCopy.remove()

        timeline.set(`#${this.maskObject.id}`, {
            x: "0px",
            onComplete: () => this.repeat(),
        })
    }

    private repeat() {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode, scrollTime } = this

        timeline.set(`#${objectId}`, { transform: "translateY(-100%)" })

        for (const animation of this.animationGroup) {
            if (animation.subTimelineType?.startsWith("scrollDown")) {
                initSubTimelineTween(animation, objectId, timeline)
            }
        }

        const oldCopy = document.getElementById(objectId + "-copy")
        if (oldCopy) oldCopy.remove()

        const numOfTextElements = textListNode?.children.length || 1
        const heightPerc = 100 / numOfTextElements

        if (textListNode) {
            const parent = textListNode.parentNode
            if (parent) {
                const copy = textListNode.cloneNode(true) as HTMLElement
                copy.id = objectId + "-copy"
                parent.appendChild(copy)

                timeline.fromTo(
                    `#${copy.id}`,
                    { transform: `translateY(${-100 + numOfTextElements * heightPerc}%)` },
                    {
                        transform: `translateY(${-100 + (numOfTextElements + 1) * heightPerc}%)`,
                        ease: "none",
                        duration: scrollTime,
                    },
                    0
                )

                timeline.set(`#${copy.id}`, { transform: "translateY(-100%)" })
            }
        }

        timeline.set(`#${objectId}`, {
            x: "0px",
            onComplete: () =>
                //check in BaseTicker.ts
                this.handleRepeat(() => this.repeat()),
        })
    }

    protected outro(newTexts?: string) {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode, maskObject, scrollPause, scrollTime } = this

        const numOfTextElements = textListNode?.children.length || 1
        const textListHeightPerc = 100 / numOfTextElements
        const delayOfLastText =
            maskObject.childIds.length * scrollTime + maskObject.childIds.length * scrollPause

        if (textListNode) {
            timeline.clear()
            timeline.set(`#${objectId}`, {
                transform: "translateY(-100%)",
            })

            timeline.fromTo(
                `#${textListNode.id}`,
                {
                    transform: `translateY(${
                        Number(-100) + Number(numOfTextElements * textListHeightPerc)
                    }%)`,
                },
                {
                    transform: `translateY(${
                        Number(-100) + Number((Number(numOfTextElements) + 1) * textListHeightPerc)
                    }%)`,
                    ease: "none",
                    duration: scrollTime,
                },
                delayOfLastText
            )

            for (const animation of this.animationGroup) {
                if (animation.subTimelineType?.startsWith("scrollDown")) {
                    initSubTimelineTween(animation, objectId, timeline)
                }
            }

            const oldCopy = document.getElementById(objectId + "-copy")
            if (oldCopy) {
                oldCopy.remove()
            }

            const parent = textListNode.parentNode
            if (parent) {
                const textList2Node = textListNode.cloneNode(true) as HTMLElement
                textList2Node.id = objectId + "-copy"

                parent.appendChild(textList2Node)
                // move last out
                timeline.fromTo(
                    `#${textList2Node.id}`,
                    {
                        transform: `translateY(${
                            Number(-100) + Number(numOfTextElements * textListHeightPerc)
                        }%)`,
                    },
                    {
                        transform: `translateY(${
                            Number(-100) +
                            Number((Number(numOfTextElements) + 1) * textListHeightPerc)
                        }%)`,
                        ease: "none",
                        duration: scrollTime,
                    },
                    0
                )
                // move last to the top
                timeline.set(`#${textList2Node.id}`, {
                    transform: "translateY(-100%)",
                })
            }
        }

        timeline.set(`#${objectId}`, {
            x: "0px",
            onComplete: () => {
                if (newTexts) {
                    this.currentJoinedTexts = newTexts
                    applyJoinedScrollTexts(textListNode, maskObject.childIds, newTexts)
                }
                this.intro()
            },
        })
    }
}
